import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { BOARD_LOCK_SERVICE_TOKEN } from '@crowdctrl/shared';

/**
 * Guard to prevent navigation when board is locked.
 * Only allows navigation to the exact locked board route.
 * Redirects to the locked board if user tries to navigate elsewhere.
 */
export const boardLockGuard: CanActivateFn = (route, state) => {
  const boardLockService = inject(BOARD_LOCK_SERVICE_TOKEN, { optional: true });
  const router = inject(Router);

  // If service is not available (not in boards app), allow navigation
  if (!boardLockService) {
    return true;
  }

  // If board is not locked, allow all navigation
  if (!boardLockService.isLocked()) {
    return true;
  }

  // Board is locked - get the stored route
  const lockedBoardRoute = boardLockService.getLockedBoardRoute();
  if (!lockedBoardRoute) {
    // Locked but no stored route - clear lock and allow navigation (safety fallback)
    boardLockService.unlock();
    return true;
  }

  // Check if trying to access the exact locked board route
  const targetUrl = state.url;
  const expectedBoardUrl = `/boards/${lockedBoardRoute.workspaceId}/${lockedBoardRoute.sessionId}/${lockedBoardRoute.boardId}`;

  if (targetUrl === expectedBoardUrl) {
    return true; // Allow navigation to the exact locked board
  }

  // Board is locked and not on the locked board - redirect to it
  router.navigateByUrl(expectedBoardUrl, { replaceUrl: true });
  return false; // Block current navigation
};
